/**
 * 設定系の Firestore 読み書きフック群。
 * 仕様書: docs/01_spec/spec_common.md C-3.2, C-1.4
 */
import type {
  SecretKey,
  SettingsGeneral,
  SettingsGoogleTag,
  SettingsLiff,
  SettingsNotifications,
  SettingsSales,
  SettingsSecretsStatus,
} from '@goenhub/shared';
import { SETTINGS_SALES_DEFAULTS } from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { useAuth } from './useAuth';

import { db, functions } from '@/lib/firebase';
import { safeSetDoc } from '@/lib/safeWrite';




const GENERAL_REF = () => doc(db, 'settings', 'general');
const NOTIFICATIONS_REF = () => doc(db, 'settings', 'notifications');
const SECRETS_STATUS_REF = () => doc(db, 'settings', 'secrets_status');
const LIFF_REF = () => doc(db, 'settings', 'liff');
const SALES_REF = () => doc(db, 'settings', 'sales');
const GOOGLE_TAG_REF = () => doc(db, 'settings', 'googleTag');

async function readDocOrNull<T>(ref: ReturnType<typeof doc>): Promise<T | null> {
  const snap = await getDoc(ref);
  return snap.exists() ? (snap.data() as T) : null;
}

// ─────────────────────────────────────────────
// 一般設定
// ─────────────────────────────────────────────
export function useGeneralSettings() {
  return useQuery({
    queryKey: ['settings', 'general'],
    queryFn: () => readDocOrNull<SettingsGeneral>(GENERAL_REF()),
  });
}

export function useUpdateGeneralSettings() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (values: Partial<SettingsGeneral>) => {
      if (!user) throw new Error('認証が必要です');
      await safeSetDoc(
        GENERAL_REF(),
        { ...values, updatedAt: serverTimestamp(), updatedBy: user.uid },
        { merge: true },
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings', 'general'] }),
  });
}

// ─────────────────────────────────────────────
// 営業設定 (settings/sales)
// 見積有効期限・請求支払期限・SA サイクル・自動メール送信元 の集約。
// 未設定 (= ドキュメント不在) の場合は SETTINGS_SALES_DEFAULTS を返す。
// ─────────────────────────────────────────────
export function useSalesSettings() {
  return useQuery({
    queryKey: ['settings', 'sales'],
    queryFn: async (): Promise<SettingsSales | null> => {
      const v = await readDocOrNull<SettingsSales>(SALES_REF());
      if (v) return v;
      return null;
    },
  });
}

/** デフォルト値で穴埋めした SettingsSales を返す（UI 表示用） */
export function useSalesSettingsWithDefaults() {
  const q = useSalesSettings();
  const merged: Omit<SettingsSales, 'updatedAt' | 'updatedBy'> = {
    ...SETTINGS_SALES_DEFAULTS,
    ...(q.data ?? {}),
  };
  return { ...q, merged };
}

export function useUpdateSalesSettings() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (
      values: Partial<Omit<SettingsSales, 'updatedAt' | 'updatedBy'>>,
    ) => {
      if (!user) throw new Error('認証が必要です');
      await safeSetDoc(
        SALES_REF(),
        { ...values, updatedAt: serverTimestamp(), updatedBy: user.uid },
        { merge: true },
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings', 'sales'] }),
  });
}

// ─────────────────────────────────────────────
// 通知設定
// ─────────────────────────────────────────────
export function useNotificationsSettings() {
  return useQuery({
    queryKey: ['settings', 'notifications'],
    queryFn: () => readDocOrNull<SettingsNotifications>(NOTIFICATIONS_REF()),
  });
}

export function useUpdateNotificationsSettings() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (values: Partial<SettingsNotifications>) => {
      if (!user) throw new Error('認証が必要です');
      await safeSetDoc(
        NOTIFICATIONS_REF(),
        { ...values, updatedAt: serverTimestamp(), updatedBy: user.uid },
        { merge: true },
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings', 'notifications'] }),
  });
}

// ─────────────────────────────────────────────
// LIFF 設定（Phase 24f）
// 読取は Firestore 直接（公開ルール）、書込は updateLiffSettings Callable 経由のみ
// ─────────────────────────────────────────────
export function useLiffSettings() {
  return useQuery({
    queryKey: ['settings', 'liff'],
    queryFn: () => readDocOrNull<SettingsLiff>(LIFF_REF()),
  });
}

interface UpdateLiffSettingsRequest {
  liffId: string;
}

interface UpdateLiffSettingsResponse {
  ok: boolean;
  liffId: string;
  changed: boolean;
}

const updateLiffSettingsFn = () =>
  httpsCallable<UpdateLiffSettingsRequest, UpdateLiffSettingsResponse>(
    functions,
    'updateLiffSettings',
  );

export function useUpdateLiffSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (liffId: string) => {
      const res = await updateLiffSettingsFn()({ liffId });
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings', 'liff'] }),
  });
}

// ─────────────────────────────────────────────
// Google タグ（GTM 等）グローバル既定 (settings/googleTag)
// read は public（公開ページが未認証で読むため）、write は owner。
// ─────────────────────────────────────────────
export function useGoogleTagSettings() {
  return useQuery({
    queryKey: ['settings', 'googleTag'],
    queryFn: () => readDocOrNull<SettingsGoogleTag>(GOOGLE_TAG_REF()),
  });
}

/** 公開ページ用: グローバル既定タグを未認証でも読む（react-query キャッシュ共有）。 */
export function usePublicGoogleTagDefault() {
  return useGoogleTagSettings();
}

export function useUpdateGoogleTagSettings() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (values: Partial<SettingsGoogleTag>) => {
      if (!user) throw new Error('認証が必要です');
      await safeSetDoc(
        GOOGLE_TAG_REF(),
        { ...values, updatedAt: serverTimestamp(), updatedBy: user.uid },
        { merge: true },
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings', 'googleTag'] }),
  });
}

// ─────────────────────────────────────────────
// シークレット状態（値ではなく「設定済みかどうか」のみ）
// ─────────────────────────────────────────────
export function useSecretsStatus() {
  return useQuery({
    queryKey: ['settings', 'secrets_status'],
    queryFn: () => readDocOrNull<SettingsSecretsStatus>(SECRETS_STATUS_REF()),
  });
}

// ─────────────────────────────────────────────
// Amazon SES 検証ステータス（getSesIdentityStatus Callable）
// 一括配信の SMTP リレー（SES）のドメイン検証状態を owner が確認するための照会。
// AWS 認証情報は ④シークレット管理（awsSes*）に登録、関数は asia-northeast1。
// ─────────────────────────────────────────────
export interface SesIdentity {
  name: string;
  type: string;
  sendingEnabled: boolean;
  verificationStatus: string;
}

export interface SesIdentityStatusResult {
  ok: boolean;
  configured: boolean;
  region: string;
  identities: SesIdentity[];
}

const getSesIdentityStatusFn = () =>
  httpsCallable<unknown, SesIdentityStatusResult>(functions, 'getSesIdentityStatus');

/** SES 検証ステータスを照会する。enabled=false なら自動取得しない（owner 以外）。 */
export function useSesIdentityStatus(enabled: boolean) {
  return useQuery({
    queryKey: ['ses', 'identityStatus'],
    queryFn: async (): Promise<SesIdentityStatusResult> => {
      const res = await getSesIdentityStatusFn()({});
      return res.data;
    },
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

export interface SecretKeyMeta {
  key: SecretKey;
  label: string;
  description: string;
  placeholder: string;
  /** キー発行元の公式 URL（ローテート時のリンク先） */
  issuerUrl: string;
  /** 発行元の表示名（ボタン文言用） */
  issuerName: string;
  /** ローテート後に再デプロイが必要な Cloud Function 名（あれば） */
  affectedFunctions?: string[];
}

export const SECRET_KEYS: SecretKeyMeta[] = [
  {
    key: 'anthropicApiKey',
    label: 'Anthropic API キー',
    description: 'Claude API 呼び出し（LINE / Mail / 広告画像生成 共通）',
    placeholder: 'sk-ant-...',
    issuerUrl: 'https://console.anthropic.com/settings/keys',
    issuerName: 'Anthropic Console',
    affectedFunctions: [
      'generateLanding',
      'regenerateLandingSection',
      'regenerateLandingAllSections',
      'regenerateLandingField',
      'generateMail',
      'generateLineNews',
      'generateAdImage',
      'optimizeAdImageText',
      'generateAdImageVariants',
      // 掲示板 AI (Phase 4)。defineSecret 消費のためローテート後の再デプロイ必須
      'assistBoardSubject',
      'onBoardPostWritten',
    ],
  },
  {
    key: 'geminiApiKey',
    label: 'Gemini API キー',
    description: '広告画像生成（Gemini banana = gemini-2.5-flash-image）',
    placeholder: 'AIza...',
    issuerUrl: 'https://aistudio.google.com/apikey',
    issuerName: 'Google AI Studio',
    affectedFunctions: ['generateAdImage', 'generateAdImageVariants'],
  },
  {
    key: 'lineChannelSecret',
    label: 'LINE Channel Secret',
    description: 'Webhook 署名検証に使用（HMAC-SHA256）',
    placeholder: '32 文字の hex',
    issuerUrl: 'https://developers.line.biz/console/',
    issuerName: 'LINE Developers Console',
    affectedFunctions: ['lineWebhook'],
  },
  {
    key: 'lineChannelAccessToken',
    label: 'LINE Channel Access Token',
    description: 'reply / push message 送信に使用',
    placeholder: 'ey...（長文字列）',
    issuerUrl: 'https://developers.line.biz/console/',
    issuerName: 'LINE Developers Console',
    affectedFunctions: ['lineWebhook', 'executeLineNewsSchedules', 'upsertContentDelivery'],
  },
  {
    key: 'gmailOAuthClientId',
    label: 'Gmail OAuth Client ID',
    description: 'Gmail API 認証（Mail 送信フロー）',
    placeholder: 'xxx.apps.googleusercontent.com',
    issuerUrl: 'https://console.cloud.google.com/apis/credentials',
    issuerName: 'Google Cloud Console',
    affectedFunctions: ['gmailOAuthStart', 'gmailOAuthCallback', 'sendMail', 'sendTestMail'],
  },
  {
    key: 'gmailOAuthClientSecret',
    label: 'Gmail OAuth Client Secret',
    description: 'Gmail API 認証に対応する秘密鍵',
    placeholder: 'GOCSPX-...',
    issuerUrl: 'https://console.cloud.google.com/apis/credentials',
    issuerName: 'Google Cloud Console',
    affectedFunctions: ['gmailOAuthStart', 'gmailOAuthCallback', 'sendMail', 'sendTestMail'],
  },
  // v3.3.3: googleChatWebhookUrl は廃止 (チャット管理タブの chat_endpoints から選択する仕様に移行)
  {
    key: 'recaptchaSiteKey',
    label: 'reCAPTCHA Enterprise Site Key',
    description: 'Firebase App Check（ブラウザ検証）',
    placeholder: '6L...',
    issuerUrl: 'https://console.cloud.google.com/security/recaptcha',
    issuerName: 'Google Cloud Console',
  },
  {
    key: 'awsSesAccessKeyId',
    label: 'AWS SES アクセスキー ID',
    description:
      'SES 検証ステータス照会用の IAM アクセスキー（ses:ListEmailIdentities）。メール送信の SMTP 認証情報とは別物。',
    placeholder: 'AKIA...（20 文字）',
    issuerUrl: 'https://console.aws.amazon.com/iam/home#/users',
    issuerName: 'AWS IAM Console',
    affectedFunctions: ['getSesIdentityStatus'],
  },
  {
    key: 'awsSesSecretAccessKey',
    label: 'AWS SES シークレットアクセスキー',
    description: 'アクセスキー ID に対応する秘密鍵（40 文字）。発行時にしか表示されないので控えること。',
    placeholder: '40 文字の base64 風文字列',
    issuerUrl: 'https://console.aws.amazon.com/iam/home#/users',
    issuerName: 'AWS IAM Console',
    affectedFunctions: ['getSesIdentityStatus'],
  },
  {
    key: 'chatworkApiToken',
    label: 'Chatwork API トークン',
    description:
      '掲示板通知を Chatwork へ送信する際の API トークン。個人別 (ユーザー詳細) と全体連絡 (通知設定) の Chatwork ルーム宛メッセージ送信に共通で使用。' +
      '※実行時に最新バージョンを動的取得するため、ローテート後の関数再デプロイは不要 (即時反映)。',
    placeholder: '40 文字の英数字トークン',
    issuerUrl: 'https://www.chatwork.com/service/packages/chatwork/subpackages/api/token.php',
    issuerName: 'Chatwork API 設定',
    // affectedFunctions は意図的に無し: 消費側 (onBoardPostCreatedNotify /
    // onBoardPostUpdatedNotify / boardDueReminder) は defineSecret ではなく
    // getSecretValue() の動的取得のため、バージョン固定の再デプロイ問題が発生しない。
  },
];
